import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { Search, UserPlus } from "lucide-react";
import { api } from "../api";
import { OWNER_MEMBERS } from "../constants/testIds";
import { useAppData } from "../contexts/AppDataContext";
import { formatCurrency, hasPermission, isPhoneQuery } from "../lib/format";
import { MEMBER_STATUS_FILTERS, membershipStatusPillProps } from "../lib/memberStatus";
import { fadeUp } from "../lib/motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { StatusPill } from "../components/common/StatusPill";
import { GymLoader } from "../components/common/GymLoader";
import { ErrorState } from "../components/common/ErrorState";
import { SortControl } from "../components/common/SortControl";
import { useSortPersistence } from "../hooks/useSortPersistence";
import { NewMemberDialog } from "../components/shell/NewMemberDialog";

const LIST_FILTER_KEYS = MEMBER_STATUS_FILTERS.map((f) => f.key);
const MEMBER_SORT_FIELDS = [{ value: "name", label: "Name (A–Z)", type: "alpha" }, { value: "dues", label: "Dues owed", type: "numeric" }];

// Real Members list for the Owner Cockpit — the drill-down destination for the cockpit's
// "Active members"/"At-risk members" tiles + the Recent Members table + Needs Attention panel's
// `?highlight=` links (all of which previously pointed at a StubPage since this screen never
// existed). `?filter=active|at_risk` presets a tab, `?highlight=<id>` scrolls to + rings a row.
// Search is debounced/live (no submit button) — results update as soon as 2+ characters typed.
// "All/Active/Frozen/Expired" (shared with the Front Desk/Manager Members screen, see
// `lib/memberStatus.js`) show by default with zero query typed; Dues-outstanding/At-risk stay
// on their own dedicated `pnl.view`/`retention.view`-gated tabs, genuinely absent (not just
// disabled) for a persona without that permission.
export default function Members() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const reducedMotion = useReducedMotion();
  const { me } = useAppData();
  const [query, setQuery] = useState("");
  const [submitted, setSubmitted] = useState("");
  const [newMemberOpen, setNewMemberOpen] = useState(false);
  const rowRefs = useRef({});
  const timerRef = useRef(null);

  const filters = useMemo(() => [
    ...MEMBER_STATUS_FILTERS,
    ...(hasPermission(me, "pnl.view") ? [{ key: "dues", label: "Dues outstanding" }] : []),
    ...(hasPermission(me, "retention.view") ? [{ key: "at_risk", label: "At-risk" }] : []),
  ], [me]);

  const filter = searchParams.get("filter") || "all";
  const highlightId = searchParams.get("highlight");

  const setFilter = (key) => {
    const next = new URLSearchParams(searchParams);
    if (key === "all") next.delete("filter"); else next.set("filter", key);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    clearTimeout(timerRef.current);
    if (query.trim().length < 2) { setSubmitted(""); return; }
    timerRef.current = setTimeout(() => setSubmitted(query.trim()), 250);
    return () => clearTimeout(timerRef.current);
  }, [query]);

  const membersQ = useQuery({
    queryKey: ["members-by-status", "owner-page", filter, submitted],
    queryFn: async () => {
      const searchParam = submitted ? (isPhoneQuery(submitted) ? { phone: submitted.replace(/[\s-]/g, "") } : { name: submitted }) : {};
      const statusParam = filter === "all" ? {} : { status: filter };
      return (await api.get("/reports/members-by-status", { params: { ...searchParam, ...statusParam, limit: 200 } })).data.members;
    },
    enabled: LIST_FILTER_KEYS.includes(filter),
  });

  const atRiskQ = useQuery({
    queryKey: ["retention-at-risk"],
    queryFn: async () => (await api.get("/retention/at-risk")).data.members || [],
    enabled: filter === "at_risk",
  });

  // Backs the "Dues outstanding" Owner Cockpit tile's drill-down — a dedicated read-model
  // (`dues_outstanding_list`, same aggregation the tile's own total is built from, so the two
  // numbers always reconcile) rather than N+1 per-member /balance calls across the whole org.
  const duesQ = useQuery({
    queryKey: ["dues-outstanding"],
    queryFn: async () => (await api.get("/reports/dues-outstanding")).data.members || [],
    enabled: filter === "dues",
  });

  const baseRows = useMemo(() => {
    if (filter === "at_risk") {
      return (atRiskQ.data || []).map((r) => ({ id: r.member_ref, name: r.member_name, phone: null, membershipStatus: "active", reason: r.reason }));
    }
    if (filter === "dues") {
      return (duesQ.data || []).map((r) => ({ id: r.member_ref, name: r.member_name, phone: r.phone, membershipStatus: r.status === "active" ? "active" : "none", dues: r.dues_balance }));
    }
    return (membersQ.data || []).map((m) => ({ id: m.id, name: m.name, phone: m.phone, membershipStatus: m.membership_status }));
  }, [filter, membersQ.data, atRiskQ.data, duesQ.data]);

  const balancesQ = useQuery({
    queryKey: ["recent-balances-owner-members", baseRows.slice(0, 50).map((r) => r.id).join(",")],
    queryFn: async () => {
      const slice = baseRows.slice(0, 50);
      const results = await Promise.allSettled(slice.map((r) => api.get(`/members/${r.id}/balance`)));
      return Object.fromEntries(
        results.filter((r) => r.status === "fulfilled").map((r) => [r.value.data.member_id, r.value.data.dues_balance]),
      );
    },
    enabled: baseRows.length > 0 && LIST_FILTER_KEYS.includes(filter),
  });

  const rows = baseRows.map((r) => (LIST_FILTER_KEYS.includes(filter) ? { ...r, dues: balancesQ.data?.[r.id] } : r));
  // DEFECT-D-29/D-30: default stays name A→Z (this is a lookup list, not a chronological feed —
  // there's no meaningful "created_at" surfaced here to default to); persisted per-screen.
  const { sortBy, sortDir, setSort } = useSortPersistence("members-list", "name", "asc");
  const sortedRows = [...rows].sort((a, b) => {
    const dir = sortDir === "asc" ? 1 : -1;
    if (sortBy === "dues") return ((a.dues || 0) - (b.dues || 0)) * dir;
    return (a.name || "").localeCompare(b.name || "") * (sortDir === "asc" ? 1 : -1);
  });

  useEffect(() => {
    if (highlightId && rowRefs.current[highlightId]) {
      rowRefs.current[highlightId].scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlightId, rows.length]);

  const isLoading = filter === "at_risk" ? atRiskQ.isLoading : filter === "dues" ? duesQ.isLoading : membersQ.isLoading;
  const hasError = filter === "at_risk" ? atRiskQ.isError : filter === "dues" ? duesQ.isError : membersQ.isError;

  return (
    <div data-testid={OWNER_MEMBERS.page} className="space-y-5 max-w-5xl">
      <motion.div {...fadeUp(reducedMotion)} className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: "var(--scarlet-text)", letterSpacing: "-0.02em" }}>Members</h1>
          <p className="text-sm mt-0.5" style={{ color: "var(--scarlet-text-secondary)" }}>Every member across your branches, in one place.</p>
        </div>
        <Button data-testid={OWNER_MEMBERS.newMemberButton} onClick={() => setNewMemberOpen(true)} className="text-white gap-2" style={{ background: "var(--scarlet-accent)" }}>
          <UserPlus size={15} /> New member
        </Button>
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.03)} className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-1.5 flex-wrap">
          {filters.map((f) => (
            <button
              key={f.key}
              data-testid={OWNER_MEMBERS.filterTab(f.key)}
              onClick={() => setFilter(f.key)}
              className="text-sm font-medium rounded-[10px] px-3.5 py-1.5 transition-colors scarlet-focusable"
              style={filter === f.key
                ? { background: "var(--scarlet-accent)", color: "#fff" }
                : { background: "transparent", border: "1px solid var(--scarlet-hairline)", color: "var(--scarlet-text-secondary)" }}
            >
              {f.label}
            </button>
          ))}
        </div>
        {filter !== "at_risk" && filter !== "dues" && (
          <div className="relative w-56">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--scarlet-text-secondary)" }} />
            <Input
              data-testid={OWNER_MEMBERS.searchInput}
              placeholder="Search name or phone…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-8"
            />
          </div>
        )}
        <SortControl fields={MEMBER_SORT_FIELDS} sortBy={sortBy} sortDir={sortDir} onChange={setSort} testId={OWNER_MEMBERS.sortControl} />
      </motion.div>

      {hasError && (
        <ErrorState
          error={filter === "at_risk" ? atRiskQ.error : filter === "dues" ? duesQ.error : membersQ.error}
          onRetry={() => (filter === "at_risk" ? atRiskQ.refetch() : filter === "dues" ? duesQ.refetch() : membersQ.refetch())}
        />
      )}

      {!hasError && isLoading && <GymLoader label="Loading members…" />}

      {!hasError && !isLoading && sortedRows.length === 0 && (
        <div className="solid-surface rounded-[14px] p-10 text-center text-sm" style={{ color: "var(--scarlet-text-secondary)" }} data-testid={OWNER_MEMBERS.emptyState}>
          {filter === "at_risk" ? "No at-risk members flagged right now." : filter === "dues" ? "No members owe outstanding dues right now." : submitted ? `No member found for "${submitted}".` : `No ${filter === "all" ? "" : filter + " "}members yet.`}
        </div>
      )}

      {!hasError && !isLoading && sortedRows.length > 0 && (
        <div className="solid-surface rounded-[14px] overflow-hidden">
          <table className="w-full text-sm" data-testid={OWNER_MEMBERS.table}>
            <thead>
              <tr className="text-left" style={{ borderBottom: "1px solid var(--scarlet-hairline)" }}>
                <th className="eyebrow px-5 py-3">Member</th>
                <th className="eyebrow px-5 py-3">{filter === "at_risk" ? "Reason" : "Phone"}</th>
                {filter !== "at_risk" && <th className="eyebrow px-5 py-3">Dues</th>}
                <th className="eyebrow px-5 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((r) => (
                <tr
                  key={r.id}
                  ref={(el) => { rowRefs.current[r.id] = el; }}
                  data-testid={OWNER_MEMBERS.row(r.id)}
                  onClick={() => navigate(`/app/members/${r.id}`)}
                  className="cursor-pointer hover:bg-black/[0.03] transition-colors"
                  style={{
                    borderBottom: "1px solid var(--scarlet-hairline)",
                    boxShadow: highlightId === r.id ? "inset 0 0 0 2px var(--scarlet-accent)" : "none",
                  }}
                >
                  <td className="px-5 py-3.5 font-medium" style={{ color: "var(--scarlet-text)" }}>{r.name}</td>
                  <td className="px-5 py-3.5" style={{ color: "var(--scarlet-text-secondary)" }}>{filter === "at_risk" ? r.reason : r.phone}</td>
                  {filter !== "at_risk" && (
                    <td className="px-5 py-3.5 tabular-nums-force font-medium" style={{ color: "var(--scarlet-text)" }}>{formatCurrency(r.dues ?? 0)}</td>
                  )}
                  <td className="px-5 py-3.5"><StatusPill {...membershipStatusPillProps(r.membershipStatus)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <NewMemberDialog
        open={newMemberOpen}
        onOpenChange={(v) => {
          setNewMemberOpen(v);
          if (!v) queryClient.invalidateQueries({ queryKey: ["members-by-status"] });
        }}
      />
    </div>
  );
}

